// BusinessAppointments.jsx
import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Tooltip,
  IconButton,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ApiServices from "../../../Services/ApiServices/ApiServices";

const BASE_URL = "http://127.0.0.1:8000"; // prefix for image paths (if required)

const BusinessAppointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const searchRef = useRef(null);

  // assign-employee dialog state
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignAppointment, setAssignAppointment] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  // snackbar
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  // read business details from sessionStorage
  let rawBiz = null;
  try {
    rawBiz = sessionStorage.getItem("businessDetails");
  } catch {
    rawBiz = null;
  }
  const businessDetails = rawBiz ? JSON.parse(rawBiz) : null;
  const businessId = businessDetails?.id ?? null;

  const fetchAppointments = async () => {
    if (!businessId) {
      setSnack({ open: true, message: "Business details not found in sessionStorage.", severity: "error" });
      return;
    }
    setLoading(true);
    try {
      const res = await ApiServices.get("service-appointments/");
      const data = res?.data ?? res;
      if (!Array.isArray(data)) {
        setAppointments([]);
        setSnack({ open: true, message: "Unexpected response from server", severity: "error" });
      } else {
        // filter to business only
        const filtered = data
          .filter((a) => {
            // some apis might return business as id or object; handle object
            if (!a.business) return false;
            if (typeof a.business === "object") return Number(a.business.id) === Number(businessId);
            return Number(a.business) === Number(businessId);
          })
          .map((a) => ({ ...a, _scheduled_at_date: a.scheduled_at ? new Date(a.scheduled_at) : null }));
        // sort newest first
        filtered.sort((x, y) => {
          const xd = x._scheduled_at_date ? x._scheduled_at_date.getTime() : 0;
          const yd = y._scheduled_at_date ? y._scheduled_at_date.getTime() : 0;
          return yd - xd;
        });
        setAppointments(filtered);
      }
    } catch (err) {
      console.error(err);
      let msg = "Failed to fetch appointments.";
      if (err?.response?.data?.detail) msg = err.response.data.detail;
      else if (err?.message) msg = err.message;
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  // debounced search (client-side filter)
  useEffect(() => {
    if (searchRef.current) clearTimeout(searchRef.current);
    searchRef.current = setTimeout(() => {
      // nothing — filtering done in render via filteredAppointments
    }, 250);
    return () => {
      if (searchRef.current) clearTimeout(searchRef.current);
    };
  }, [search]);

  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  // helper to format datetime
  const formatDateTime = (iso) => {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const hh = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      return `${y}-${m}-${day} ${hh}:${mm}`;
    } catch {
      return iso;
    }
  };

  // row color logic:
  // completed/paid -> green; no employee -> red; employee assigned but payment != PAID -> yellow
  const rowBgFor = (a) => {
    const paid = (a.payment_status ?? "").toUpperCase() === "PAID";
    const completed = (a.status ?? "").toUpperCase() === "COMPLETED";
    if (paid || completed) return "#e8fff0"; // green
    if (!a.employee) return "#ffe8e8"; // red
    // employee present but not paid
    return "#fff8e1"; // yellow
  };

  // open assign dialog
  const openAssignDialog = async (appointment) => {
    setAssignAppointment(appointment);
    setSelectedEmployeeId("");
    // fetch employees for the business
    await fetchEmployeesForBusiness();
    setAssignOpen(true);
  };

  const closeAssignDialog = () => {
    setAssignOpen(false);
    setAssignAppointment(null);
    setSelectedEmployeeId("");
    setAssignLoading(false);
  };

  // fetch employees and filter by same business
  const fetchEmployeesForBusiness = async () => {
    setLoadingEmployees(true);
    try {
      const res = await ApiServices.get("external-employees/");
      const data = res?.data ?? res;
      const all = Array.isArray(data) ? data : [];
      const filtered = all.filter((e) => {
        if (!e) return false;
        if (typeof e.business === "number") return Number(e.business) === Number(businessId);
        if (typeof e.business === "object" && e.business !== null) return Number(e.business.id) === Number(businessId);
        return false;
      });
      setEmployees(filtered);
    } catch (err) {
      console.error(err);
      setSnack({ open: true, message: "Failed to load employees", severity: "error" });
    } finally {
      setLoadingEmployees(false);
    }
  };

  // assign employee action
  const submitAssignEmployee = async () => {
    if (!assignAppointment) return;
    if (!selectedEmployeeId) {
      setSnack({ open: true, message: "Please select an employee", severity: "error" });
      return;
    }
    setAssignLoading(true);
    try {
      const payload = { employee_id: Number(selectedEmployeeId) };
      await ApiServices.post(`service-appointments/${assignAppointment.id}/assign-employee/`, payload);

      setSnack({ open: true, message: "Employee assigned successfully", severity: "success" });
      closeAssignDialog();
      // refresh latest appointments from server
      await fetchAppointments();
    } catch (err) {
      console.error(err);
      let msg = "Failed to assign employee.";
      if (err?.response?.data?.detail) msg = err.response.data.detail;
      else if (err?.message) msg = err.message;
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setAssignLoading(false);
    }
  };

  // open image in new tab
  const openImage = (path) => {
    const url = path?.startsWith("http") ? path : `${BASE_URL}${path}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // client-side filtered appointments based on search
  const filteredAppointments = appointments.filter((a) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    const cust = `${a.customer?.first_name ?? ""} ${a.customer?.last_name ?? ""}`.toLowerCase();
    const emp = `${a.employee?.first_name ?? ""} ${a.employee?.last_name ?? ""}`.toLowerCase();
    const status = (a.status ?? "").toLowerCase();
    const phone = (a.customer?.phone ?? a.employee?.phone ?? "").toLowerCase();
    const addr = (a.address ?? a.customer?.address ?? "").toLowerCase();
    return (
      String(a.id).includes(q) ||
      cust.includes(q) ||
      emp.includes(q) ||
      status.includes(q) ||
      phone.includes(q) ||
      addr.includes(q)
    );
  });

  return (
    <Box sx={{ p: 3, width: "100%", boxSizing: "border-box", background: "#fff", minHeight: "70vh" }}>
      <style>{`
        .header { display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; margin-bottom:16px; }
        .thumb { width:64px; height:48px; object-fit:cover; border-radius:6px; border:1px solid rgba(0,0,0,0.08); }
      `}</style>

      <Box className="header">
        <Box>
          <Typography variant="h5" sx={{ color: "#094c9f", fontWeight: 700 }}>
            Business Appointments
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {businessDetails ? businessDetails.name : "No business in sessionStorage"}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Search id / customer / employee / status..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 320 }}
          />
          <Button variant="contained" onClick={fetchAppointments} sx={{ backgroundColor: "#094c9f" }}>
            Refresh
          </Button>
        </Box>
      </Box>

      <Paper sx={{ width: "100%", overflow: "hidden" }} elevation={2}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: "70vh", overflowX: "auto" }}>
            <Table stickyHeader sx={{ minWidth: 900, tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 60 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 150 }}>Scheduled</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 220 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 160 }}>Employee</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 120 }}>Services</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 110 }}>Total</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 120 }}>Payment</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 180 }}>Images</TableCell>
                 
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredAppointments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No appointments found for this business.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAppointments.map((a, idx) => {
                    const services = Array.isArray(a.appointment_services) ? a.appointment_services : [];
                    const images = [];
                    services.forEach((s) => {
                      if (Array.isArray(s.images) && s.images.length) {
                        s.images.forEach((im) => images.push(im));
                      }
                    });

                    const bg = rowBgFor(a);

                    return (
                      <TableRow key={a.id} hover sx={{ backgroundColor: bg }}>
                        <TableCell>{idx + 1}</TableCell>

                        <TableCell>
                          <Typography variant="body2">{formatDateTime(a.scheduled_at)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {a.status ?? "-"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontWeight: 700 }}>
                            {a.customer_details ? `${a.customer_details.first_name ?? ""} ${a.customer_details.last_name ?? ""}` : "-"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {a.customer_details?.email ?? ""} • {a.customer_details?.phone ?? ""}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          {a.employee_details ? (
                            <>
                              <Typography sx={{ fontWeight: 700 }}>{`${a.employee_details.first_name} ${a.employee_details.last_name}`}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {a.employee_details.email} • {a.employee_details.phone}
                              </Typography>
                            </>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<PersonAddIcon />}
                              onClick={() => openAssignDialog(a)}
                              sx={{ textTransform: "none" }}
                            >
                              Assign Employee
                            </Button>
                          )}
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontWeight: 700 }}>{services.length}</Typography>
                          {services.map((s) => (
                            <Typography key={s.id} variant="caption" color="text.secondary" display="block">
                              Service #{s.service} — {parseFloat(s.price_snapshot ?? 0).toFixed(2)}
                            </Typography>
                          ))}
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontWeight: 700 }}>{parseFloat(a.total_price ?? 0).toFixed(2)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Admin cost: {parseFloat(a.admin_cost_to_business ?? 0).toFixed(2)}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontWeight: 700 }}>{a.payment_status ?? "-"}</Typography>
                        </TableCell>

                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                            {images.length === 0 ? (
                              <Typography variant="caption" color="text.secondary">
                                No images
                              </Typography>
                            ) : (
                              images.map((im) => {
                                const url = im.image?.startsWith("http") ? im.image : `${BASE_URL}${im.image}`;
                                const label = im.is_before ? "Before" : "After";
                                return (
                                  <Tooltip key={im.id} title={`${label} • ${im.uploaded_by}`}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                      <img
                                        src={url}
                                        alt={label}
                                        className="thumb"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => openImage(im.image)}
                                      />
                                      <IconButton size="small" onClick={() => openImage(im.image)} aria-label="open">
                                        <OpenInNewIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </Tooltip>
                                );
                              })
                            )}
                          </Box>
                        </TableCell>

                        
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Assign Employee Dialog */}
      <Dialog open={assignOpen} onClose={closeAssignDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Employee</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="employee-select-label">{loadingEmployees ? "Loading..." : "Employee"}</InputLabel>
              <Select
                labelId="employee-select-label"
                value={selectedEmployeeId}
                label="Employee"
                onChange={(e) => setSelectedEmployeeId(e.target.value)}
              >
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name} — {emp.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssignDialog} disabled={assignLoading}>
            Cancel
          </Button>
          <Button onClick={submitAssignEmployee} variant="contained" disabled={assignLoading} sx={{ backgroundColor: "#094c9f" }}>
            {assignLoading ? <CircularProgress size={18} /> : "Assign"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4500} onClose={closeSnack}>
        <Alert onClose={closeSnack} severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BusinessAppointments;
